import { useState, useEffect, useCallback, useRef } from 'react'
import { useParams } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api'
import { Button } from '@/components/ui/button'
import { toast } from '@/components/ui/toaster'
import { cn } from '@/lib/utils'
import {
    FileDown,
    Loader2,
    Undo,
    Redo,
    Bold,
    Italic,
    Underline,
    Strikethrough,
    MessageSquare,
    History,
    Check,
    Cloud,
    CloudOff,
    Maximize,
    Minimize,
} from 'lucide-react'
import { HistoryPanel } from '@/components/editor/HistoryPanel'
import { DiffViewer } from '@/components/editor/DiffViewer'

interface ScriptEditorContentProps {
    draftId: string
}

interface Script {
    id: string
    title: string
    author: string | null
    logline: string | null
    version: number
    content: ScriptContent
    status: string
    wordCount: number
    pageCount: number
    updatedAt: number
    draftId?: string | null
}

interface ScriptContent {
    elements: ScriptElement[]
    metadata: {
        title: string
        author: string
        copyright?: string
        contact?: string
        date?: string
        draft?: string
    }
}

interface ScriptElement {
    id: string
    type: ElementType
    content: string
    metadata?: Record<string, unknown>
}

type ElementType = 'scene_heading' | 'action' | 'character' | 'dialogue' | 'parenthetical' | 'transition' | 'shot' | 'note' | 'centered'

// Fountain format detection patterns
const SCENE_HEADING_PATTERN = /^(INT\.|EXT\.|INT\/EXT\.|I\/E\.|EST\.)/i
const TRANSITION_PATTERN = /^.*(CUT TO:|FADE TO:|FADE OUT\.|FADE IN:|DISSOLVE TO:|SMASH CUT TO:|MATCH CUT TO:|JUMP CUT TO:)$/i

const ELEMENT_LABELS: Record<ElementType, string> = {
    scene_heading: 'Scene',
    action: 'Action',
    character: 'Character',
    dialogue: 'Dialogue',
    parenthetical: 'Paren',
    transition: 'Transition',
    shot: 'Shot',
    note: 'Note',
    centered: 'Centered',
}

// Common scene heading prefixes for SmartType
const SCENE_PREFIXES = ['INT. ', 'EXT. ', 'INT./EXT. ', 'I/E. ', 'EST. ']

// Common transitions for SmartType
const COMMON_TRANSITIONS = ['CUT TO:', 'FADE TO:', 'FADE OUT.', 'FADE IN:', 'DISSOLVE TO:', 'SMASH CUT TO:', 'MATCH CUT TO:', 'JUMP CUT TO:']

// Tab only cycles between Action and Character
const TAB_CYCLE: ElementType[] = ['action', 'character']

// Page sizing - larger pages that fill more viewport
const PAGE_WIDTH_PX = 900
const PAGE_HEIGHT_PX = 1150
const PAGE_PADDING_TOP = 64
const PAGE_PADDING_BOTTOM = 64
const PAGE_PADDING_LEFT = 96
const PAGE_PADDING_RIGHT = 64
const LINE_HEIGHT = 18
const LINES_PER_PAGE = Math.floor((PAGE_HEIGHT_PX - PAGE_PADDING_TOP - PAGE_PADDING_BOTTOM) / LINE_HEIGHT)

// Export to Fountain format
function exportToFountain(elements: ScriptElement[], title?: string, author?: string): string {
    let fountain = ''
    if (title) {
        fountain += `Title: ${title}\n`
        if (author) fountain += `Author: ${author}\n`
        fountain += `Draft date: ${new Date().toLocaleDateString()}\n`
        fountain += '\n'
    }

    for (const el of elements) {
        switch (el.type) {
            case 'scene_heading':
                fountain += `\n${el.content.toUpperCase()}\n\n`
                break
            case 'action':
                fountain += `${el.content}\n\n`
                break
            case 'character':
                fountain += `\n${el.content.toUpperCase()}\n`
                break
            case 'dialogue':
                fountain += `${el.content}\n\n`
                break
            case 'parenthetical':
                fountain += `${el.content.startsWith('(') ? el.content : '(' + el.content + ')'}\n`
                break
            case 'transition':
                fountain += `\n> ${el.content.toUpperCase()}\n\n`
                break
            case 'centered':
                fountain += `\n> ${el.content} <\n\n`
                break
            case 'note':
                fountain += `[[${el.content}]]\n\n`
                break
            default:
                fountain += `${el.content}\n\n`
        }
    }
    return fountain.trim() + '\n'
}

// Compute scene numbers from elements
function computeSceneNumbers(elements: ScriptElement[]): Map<string, number> {
    const map = new Map<string, number>()
    let sceneNum = 0
    for (const el of elements) {
        if (el.type === 'scene_heading') {
            sceneNum++
            map.set(el.id, sceneNum)
        }
    }
    return map
}

// Detect if same character speaks again after non-dialogue interruption
function shouldShowContd(elements: ScriptElement[], currentIndex: number): boolean {
    const el = elements[currentIndex]
    if (el.type !== 'character') return false
    const charName = el.content.toUpperCase().replace(/\s*\(.*\)\s*$/, '').trim()
    // Look backwards for previous character element
    for (let i = currentIndex - 1; i >= 0; i--) {
        const prev = elements[i]
        if (prev.type === 'character') {
            const prevName = prev.content.toUpperCase().replace(/\s*\(.*\)\s*$/, '').trim()
            return prevName === charName
        }
        // If we hit a scene heading, stop looking
        if (prev.type === 'scene_heading') return false
        // Skip action lines (dialogue was interrupted by action)
        if (prev.type === 'action' || prev.type === 'transition') continue
        // Skip dialogue/parenthetical of previous character
        if (prev.type === 'dialogue' || prev.type === 'parenthetical') continue
    }
    return false
}

export default function ScriptEditorContent({ draftId }: ScriptEditorContentProps) {
    const { projectId } = useParams<{ projectId: string }>()
    const editorContainerRef = useRef<HTMLDivElement>(null)
    const queryClient = useQueryClient()
    const autoCreateAttempted = useRef(false)

    // Script ID is provided by parent (DraftEditorPage) usually, but here draftId IS the script identifier for the editor?
    // In projects.ts, routes are /projects/:projectId/scripts.
    // However, ScriptEditorContent takes `draftId`. 
    // Usually a draft HAS a script (1:1).
    // Note: In `scripts.ts`: `scripts.get('/projects/:projectId/scripts', ...)` lists scripts.
    // BUT `get('/projects/:projectId/scripts/:scriptId')` gets a specific one.
    // The `draftId` prop might be the Draft ID (from drafts table), not the Script ID.
    // The `scripts` table has `draft_id`.
    // We need the SCRIPT ID to call the version endpoints.
    // We can fetch the script using the draftId: `GET /projects/:projectId/drafts/:draftId/script`? 
    // OR we filter scripts list by draft_id.
    // Let's assume for now we resolve scriptId from the `draft-script` query below.

    // Fetch scripts to find the one matching this draft
    const { data: scriptsResponse } = useQuery({
        queryKey: ['scripts', projectId],
        queryFn: async () => {
            const res = await api.get<{ data: Script[] }>(`/projects/${projectId}/scripts`)
            return res.data
        },
        enabled: !!projectId,
    })

    // Fetch drafts to check if this is a branch
    const { data: draftsResponse } = useQuery({
        queryKey: ['drafts', projectId],
        queryFn: async () => {
            const res = await api.get<{ data: any[] }>(`/projects/${projectId}/drafts`)
            return res.data
        },
        enabled: !!projectId
    })

    const currentDraft = draftsResponse?.data?.find(d => d.id === draftId)
    const isBranch = !!currentDraft?.parentDraftId

    // Find the current script
    const currentScript = scriptsResponse?.data?.find(s => s.draftId === draftId) || scriptsResponse?.data?.[0]

    const [isMergeView, setIsMergeView] = useState(false)
    const [parentScriptContent, setParentScriptContent] = useState<ScriptElement[] | null>(null)


    const scriptId = currentScript?.id
    const storageKey = `script_${scriptId || draftId}`

    // Load initial state from localStorage as immediate fallback
    const [elements, setElements] = useState<ScriptElement[]>(() => {
        try {
            const stored = localStorage.getItem(`${storageKey}_elements`)
            if (stored) {
                return JSON.parse(stored)
            }
        } catch (e) {
            console.warn('Failed to load elements from localStorage:', e)
        }
        return [{ id: crypto.randomUUID(), type: 'action' as ElementType, content: '' }]
    })
    const [hasLoadedFromApi, setHasLoadedFromApi] = useState(false)
    const [selectedElement, setSelectedElement] = useState<string | null>(null)
    const [selectedElements, setSelectedElements] = useState<Set<string>>(new Set())
    const [isDragging, setIsDragging] = useState(false)
    const [selectionStart, setSelectionStart] = useState<string | null>(null)
    const [hasChanges, setHasChanges] = useState(false)

    // Auto-save state
    const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle')
    const [lastSavedAt, setLastSavedAt] = useState<Date | null>(null)
    const saveTimeoutRef = useRef<NodeJS.Timeout | null>(null)

    // Load from API when script data becomes available — takes priority over localStorage
    useEffect(() => {
        if (currentScript && !hasLoadedFromApi) {
            try {
                const content = currentScript.content
                if (content && content.elements && content.elements.length > 0) {
                    setElements(content.elements)
                    setHasLoadedFromApi(true)
                    // Also update localStorage with API data
                    localStorage.setItem(`${storageKey}_elements`, JSON.stringify(content.elements))
                } else {
                    setHasLoadedFromApi(true)
                }
            } catch (e) {
                console.warn('Failed to load from API script content:', e)
                setHasLoadedFromApi(true)
            }
        }
    }, [currentScript, hasLoadedFromApi, storageKey])

    // Auto-create a script for this draft if none exists in DB
    useEffect(() => {
        console.log('[Keyframes] Auto-create check:', { autoCreateAttempted: autoCreateAttempted.current, scriptsResponse: !!scriptsResponse, projectId, draftId })
        if (autoCreateAttempted.current) { console.log('[Keyframes] Skipping: already attempted'); return }
        if (!scriptsResponse) { console.log('[Keyframes] Skipping: scriptsResponse not loaded'); return }
        if (!projectId) { console.log('[Keyframes] Skipping: no projectId'); return }
        if (!draftId) { console.log('[Keyframes] Skipping: no draftId'); return }

        // scriptsResponse is the queryFn result which is res.data (= the scripts array from api wrapper)
        const allScripts = scriptsResponse?.data || scriptsResponse || []
        const scriptsList = Array.isArray(allScripts) ? allScripts : []
        console.log('[Keyframes] Scripts for project:', scriptsList.length, 'looking for draftId:', draftId)
        const existingScript = scriptsList.find((s: any) => s.draftId === draftId)
        if (existingScript) { console.log('[Keyframes] Script already exists for draft'); return }

        autoCreateAttempted.current = true
        console.log('[Keyframes] Auto-creating script for draft:', draftId)

        // Create a script for this draft
        const createScript = async () => {
            try {
                await api.post(`/projects/${projectId}/scripts`, {
                    title: 'Untitled Script',
                    draftId: draftId,
                })
                console.log('[Keyframes] Script auto-created successfully')
                // Refresh the scripts list
                queryClient.invalidateQueries({ queryKey: ['scripts', projectId] })
            } catch (e) {
                console.error('[Keyframes] Failed to auto-create script for draft:', e)
            }
        }
        createScript()
    }, [scriptsResponse, projectId, draftId, queryClient])


    // History panel state
    const [showHistory, setShowHistory] = useState(false)
    const [selectedCheckpoint, setSelectedCheckpoint] = useState<string | null>(null)
    const [previewSnapshot, setPreviewSnapshot] = useState<ScriptElement[] | null>(null)

    // Undo/Redo history
    const [history, setHistory] = useState<ScriptElement[][]>(() => [elements])
    const [historyIndex, setHistoryIndex] = useState(0)

    // Focus mode state
    const [focusMode, setFocusMode] = useState(false)

    // SmartType autocomplete state
    const [autocompleteVisible, setAutocompleteVisible] = useState(false)
    const [autocompleteSuggestions, setAutocompleteSuggestions] = useState<string[]>([])
    const [autocompleteIndex, setAutocompleteIndex] = useState(0)
    const [autocompleteTargetId, setAutocompleteTargetId] = useState<string | null>(null)
    const autocompleteRef = useRef<HTMLDivElement>(null)

    // Compute scene numbers
    const sceneNumbers = computeSceneNumbers(previewSnapshot || elements)

    // Extract known character names and scene headings for SmartType
    const knownCharacterNames = [...new Set(
        elements
            .filter(el => el.type === 'character' && el.content.trim())
            .map(el => el.content.toUpperCase().replace(/\s*\(.*\)\s*$/, '').trim())
    )]
    const knownSceneHeadings = [...new Set(
        elements
            .filter(el => el.type === 'scene_heading' && el.content.trim())
            .map(el => el.content.toUpperCase())
    )]


    // Persist elements to localStorage whenever they change
    useEffect(() => {
        localStorage.setItem(`${storageKey}_elements`, JSON.stringify(elements))
    }, [elements, storageKey])



    // Auto-save effect with debounce
    useEffect(() => {
        if (!hasChanges) return

        // Clear existing timeout
        if (saveTimeoutRef.current) {
            clearTimeout(saveTimeoutRef.current)
        }

        // Debounce save for 1.5 seconds
        saveTimeoutRef.current = setTimeout(() => {
            performAutoSave()
        }, 1500)

        return () => {
            if (saveTimeoutRef.current) {
                clearTimeout(saveTimeoutRef.current)
            }
        }
    }, [elements, hasChanges])

    // Perform auto-save
    const performAutoSave = useCallback(async () => {
        if (!scriptId) return
        setSaveStatus('saving')
        try {
            // Save to API — also ensure draftId is set on the script
            const patchPayload: Record<string, any> = {
                content: JSON.stringify({ elements }),
            }
            // If this script doesn't yet have a draftId, set it
            if (currentScript && !currentScript.draftId) {
                patchPayload.draftId = draftId
            }
            await api.patch(`/projects/${projectId}/scripts/${scriptId}`, patchPayload);

            setSaveStatus('saved')
            setHasChanges(false)
            setLastSavedAt(new Date())

            // Reset to idle after 2 seconds
            setTimeout(() => setSaveStatus('idle'), 2000)
        } catch {
            setSaveStatus('error')
            toast({ title: 'Save failed', description: 'Please try again or check your connection.' })
        }
    }, [elements, scriptId, projectId, draftId, currentScript])

    // Auto-resize textareas
    const handleTextareaInput = useCallback((e: React.FormEvent<HTMLTextAreaElement>) => {
        const textarea = e.currentTarget
        textarea.style.height = 'auto'
        textarea.style.height = `${textarea.scrollHeight}px`
    }, [])

    // Generate unique ID
    const generateId = () => crypto.randomUUID()

    // Push to history
    const pushToHistory = useCallback((newElements: ScriptElement[]) => {
        setHistory(prev => {
            const newHistory = prev.slice(0, historyIndex + 1)
            newHistory.push(JSON.parse(JSON.stringify(newElements)))
            // Keep max 50 history entries
            if (newHistory.length > 50) newHistory.shift()
            return newHistory
        })
        setHistoryIndex(prev => Math.min(prev + 1, 49))
    }, [historyIndex])

    // Undo
    const handleUndo = useCallback(() => {
        if (historyIndex > 0) {
            setHistoryIndex(prev => prev - 1)
            setElements(JSON.parse(JSON.stringify(history[historyIndex - 1])))
            setHasChanges(true)
        }
    }, [historyIndex, history])

    // Redo
    const handleRedo = useCallback(() => {
        if (historyIndex < history.length - 1) {
            setHistoryIndex(prev => prev + 1)
            setElements(JSON.parse(JSON.stringify(history[historyIndex + 1])))
            setHasChanges(true)
        }
    }, [historyIndex, history])

    // Delete selected elements
    const deleteSelectedElements = useCallback(() => {
        if (selectedElements.size === 0) return

        let newElements = elements.filter(el => !selectedElements.has(el.id))
        if (newElements.length === 0) {
            newElements = [{ id: crypto.randomUUID(), type: 'action' as ElementType, content: '' }]
        }
        setElements(newElements)
        setSelectedElements(new Set())
        setSelectedElement(null)
        setHasChanges(true)
        pushToHistory(newElements)

        toast({ title: `Deleted ${selectedElements.size} element(s)` })
    }, [selectedElements, elements])

    // Global keyboard shortcuts
    useEffect(() => {
        const handleGlobalKeyDown = (e: KeyboardEvent) => {
            // Undo: Cmd/Ctrl+Z
            if ((e.metaKey || e.ctrlKey) && e.key === 'z' && !e.shiftKey) {
                e.preventDefault()
                handleUndo()
                return
            }
            // Redo: Cmd/Ctrl+Shift+Z or Cmd/Ctrl+Y
            if ((e.metaKey || e.ctrlKey) && ((e.key === 'z' && e.shiftKey) || e.key === 'y')) {
                e.preventDefault()
                handleRedo()
                return
            }
            // Focus mode: Cmd/Ctrl+D
            if ((e.metaKey || e.ctrlKey) && e.key === 'd') {
                e.preventDefault()
                setFocusMode(prev => !prev)
                return
            }
            // Select All: Cmd/Ctrl+A
            if ((e.metaKey || e.ctrlKey) && e.key === 'a') {
                e.preventDefault()
                setSelectedElements(new Set(elements.map(el => el.id)))
                return
            }
            // Delete selected: Delete or Backspace when multiple selected
            if ((e.key === 'Delete' || e.key === 'Backspace') && selectedElements.size > 1) {
                e.preventDefault()
                deleteSelectedElements()
                return
            }
        }

        const handleGlobalMouseUp = () => {
            setIsDragging(false)
            setSelectionStart(null)
        }

        window.addEventListener('keydown', handleGlobalKeyDown)
        window.addEventListener('mouseup', handleGlobalMouseUp)
        return () => {
            window.removeEventListener('keydown', handleGlobalKeyDown)
            window.removeEventListener('mouseup', handleGlobalMouseUp)
        }
    }, [handleUndo, handleRedo, elements, selectedElements, deleteSelectedElements, focusMode])

    // Handle drag selection
    const handleDragStart = (elementId: string) => {
        setIsDragging(true)
        setSelectionStart(elementId)
        setSelectedElements(new Set([elementId]))
        setSelectedElement(null) // Clear focused element

        // Remove focus from any active textarea
        if (document.activeElement instanceof HTMLElement) {
            document.activeElement.blur()
        }
    }

    const handleDragEnter = (elementId: string) => {
        if (isDragging && selectionStart) {
            const startIndex = elements.findIndex(el => el.id === selectionStart)
            const currentIndex = elements.findIndex(el => el.id === elementId)

            if (startIndex === -1 || currentIndex === -1) return

            const start = Math.min(startIndex, currentIndex)
            const end = Math.max(startIndex, currentIndex)

            const newSelection = new Set<string>()
            for (let i = start; i <= end; i++) {
                newSelection.add(elements[i].id)
            }
            setSelectedElements(newSelection)
        }
    }

    // Update element content with SmartType suggestions
    const updateElement = (id: string, content: string) => {
        const newElements = elements.map(el =>
            el.id === id ? { ...el, content } : el
        )
        setElements(newElements)
        setHasChanges(true)

        // Auto-detect element type (only for unambiguous patterns)
        const element = newElements.find(el => el.id === id)
        if (element) {
            let detectedType: ElementType | null = null

            if (SCENE_HEADING_PATTERN.test(content)) {
                detectedType = 'scene_heading'
            } else if (TRANSITION_PATTERN.test(content)) {
                detectedType = 'transition'
            } else if (content.startsWith('(') && content.endsWith(')') && content.length > 2) {
                detectedType = 'parenthetical'
            }

            if (detectedType && detectedType !== element.type) {
                const updatedElements = newElements.map(el =>
                    el.id === id ? { ...el, type: detectedType! } : el
                )
                setElements(updatedElements)
            }

            // SmartType: generate autocomplete suggestions
            const upperContent = content.toUpperCase()
            let suggestions: string[] = []

            if (element.type === 'character' && content.length >= 1) {
                suggestions = knownCharacterNames.filter(
                    name => name.startsWith(upperContent) && name !== upperContent
                )
            } else if (element.type === 'scene_heading' && content.length >= 2) {
                // Suggest known scene headings
                suggestions = knownSceneHeadings.filter(
                    heading => heading.startsWith(upperContent) && heading !== upperContent
                )
                // Also suggest prefixes if just starting
                if (suggestions.length === 0 && content.length < 5) {
                    suggestions = SCENE_PREFIXES.filter(
                        p => p.toUpperCase().startsWith(upperContent)
                    )
                }
            } else if (element.type === 'transition' && content.length >= 1) {
                suggestions = COMMON_TRANSITIONS.filter(
                    t => t.toUpperCase().startsWith(upperContent)
                )
            }

            if (suggestions.length > 0) {
                setAutocompleteSuggestions(suggestions.slice(0, 6))
                setAutocompleteIndex(0)
                setAutocompleteTargetId(id)
                setAutocompleteVisible(true)
            } else {
                setAutocompleteVisible(false)
            }
        }
    }


    // Change element type
    const changeElementType = (id: string, type: ElementType) => {
        const newElements = elements.map(el =>
            el.id === id ? { ...el, type } : el
        )
        setElements(newElements)
        setHasChanges(true)
        pushToHistory(newElements)

    }


    // Handle key events
    const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>, elementId: string, index: number) => {
        const element = elements[index]
        const textarea = e.currentTarget
        const cursorPos = textarea.selectionStart

        // SmartType: handle autocomplete navigation
        if (autocompleteVisible && autocompleteTargetId === elementId) {
            if (e.key === 'ArrowDown') {
                e.preventDefault()
                setAutocompleteIndex(prev => Math.min(prev + 1, autocompleteSuggestions.length - 1))
                return
            }
            if (e.key === 'ArrowUp') {
                e.preventDefault()
                setAutocompleteIndex(prev => Math.max(prev - 1, 0))
                return
            }
            if (e.key === 'Tab' || e.key === 'Enter') {
                if (autocompleteSuggestions[autocompleteIndex]) {
                    e.preventDefault()
                    const suggestion = autocompleteSuggestions[autocompleteIndex]
                    updateElement(elementId, suggestion)
                    setAutocompleteVisible(false)
                    // Move cursor to end
                    setTimeout(() => {
                        textarea.setSelectionRange(suggestion.length, suggestion.length)
                    }, 0)
                    return
                }
            }
            if (e.key === 'Escape') {
                e.preventDefault()
                setAutocompleteVisible(false)
                return
            }
        }

        // Tab: cycle between Action and Character
        if (e.key === 'Tab' && !e.shiftKey) {
            e.preventDefault()
            if (element.content === '') {
                const currentIndex = TAB_CYCLE.indexOf(element.type as ElementType)
                const nextType = TAB_CYCLE[(currentIndex + 1) % TAB_CYCLE.length]
                changeElementType(elementId, nextType)
            } else if (element.type === 'character') {
                // Create parenthetical after character name
                const newElement: ScriptElement = {
                    id: generateId(),
                    type: 'parenthetical',
                    content: '',
                }
                const newElements = [...elements]
                newElements.splice(index + 1, 0, newElement)
                setElements(newElements)
                pushToHistory(newElements)
                setTimeout(() => {
                    const nextTextarea = document.querySelector(`[data-element-id="${newElement.id}"]`) as HTMLTextAreaElement
                    nextTextarea?.focus()
                }, 0)
            }
            return
        }

        // Enter: create new element
        if (e.key === 'Enter') {
            e.preventDefault()
            let nextType: ElementType = 'action'

            // Context-aware next type (Arc Studio behavior)
            if (element.type === 'character') {
                nextType = 'dialogue'
            } else if (element.type === 'dialogue') {
                // Arc Studio: after dialogue, next element is Character (for back-and-forth)
                nextType = 'character'
            } else if (element.type === 'parenthetical') {
                nextType = 'dialogue'
            } else if (element.type === 'scene_heading') {
                nextType = 'action'
            }

            // Split at cursor if in middle of text
            const beforeCursor = element.content.slice(0, cursorPos)
            const afterCursor = element.content.slice(cursorPos)

            if (afterCursor) {
                // Split element
                const newElements = [...elements]
                newElements[index] = { ...element, content: beforeCursor }
                newElements.splice(index + 1, 0, {
                    id: generateId(),
                    type: nextType,
                    content: afterCursor,
                })
                setElements(newElements)
                pushToHistory(newElements)
            } else {
                // Create new empty element
                const newElement: ScriptElement = {
                    id: generateId(),
                    type: nextType,
                    content: '',
                }
                const newElements = [...elements]
                newElements.splice(index + 1, 0, newElement)
                setElements(newElements)
                pushToHistory(newElements)
            }

            setTimeout(() => {
                const nextIndex = index + 1
                if (nextIndex < elements.length + 1) {
                    const nextTextarea = document.querySelectorAll('[data-element-id]')[nextIndex] as HTMLTextAreaElement
                    nextTextarea?.focus()
                }
            }, 0)
            return
        }

        // Backspace at start: merge with previous or delete
        if (e.key === 'Backspace' && cursorPos === 0) {
            if (element.content === '' && elements.length > 1) {
                e.preventDefault()
                const newElements = elements.filter(el => el.id !== elementId)
                setElements(newElements)
                pushToHistory(newElements)
                if (index > 0) {
                    setTimeout(() => {
                        const prevTextarea = document.querySelectorAll('[data-element-id]')[index - 1] as HTMLTextAreaElement
                        prevTextarea?.focus()
                        prevTextarea?.setSelectionRange(prevTextarea.value.length, prevTextarea.value.length)
                    }, 0)
                }
            } else if (index > 0) {
                e.preventDefault()
                const prevElement = elements[index - 1]
                const prevLength = prevElement.content.length
                const newElements = [...elements]
                newElements[index - 1] = {
                    ...prevElement,
                    content: prevElement.content + element.content,
                }
                newElements.splice(index, 1)
                setElements(newElements)
                pushToHistory(newElements)
                setTimeout(() => {
                    const prevTextarea = document.querySelectorAll('[data-element-id]')[index - 1] as HTMLTextAreaElement
                    prevTextarea?.focus()
                    prevTextarea?.setSelectionRange(prevLength, prevLength)
                }, 0)
            }
        }
    }

    // Paginate elements
    const paginateElements = useCallback(() => {
        // Use preview content when in preview mode
        const elementsToUse = previewSnapshot || elements
        const pages: ScriptElement[][] = []
        let currentPage: ScriptElement[] = []
        let currentLineCount = 0

        elementsToUse.forEach(element => {
            const lineCount = Math.ceil(element.content.length / 60) || 1
            if (currentLineCount + lineCount > LINES_PER_PAGE && currentPage.length > 0) {
                pages.push(currentPage)
                currentPage = []
                currentLineCount = 0
            }
            currentPage.push(element)
            currentLineCount += lineCount
        })

        if (currentPage.length > 0) {
            pages.push(currentPage)
        }

        return pages.length > 0 ? pages : [[]]
    }, [elements, previewSnapshot])

    // Get element styles
    const getElementStyles = (type: ElementType): string => {
        switch (type) {
            case 'scene_heading':
                return 'uppercase font-bold tracking-wide w-full'
            case 'action':
                return 'w-full'
            case 'character':
                return 'uppercase w-[40%]'
            case 'dialogue':
                return 'w-[60%]'
            case 'parenthetical':
                return 'italic text-sm w-[35%]'
            case 'transition':
                return 'uppercase text-right w-full'
            case 'centered':
                return 'text-center w-full'
            default:
                return 'w-full'
        }
    }

    // Get container styles for centering
    const getElementContainerStyles = (type: ElementType): string => {
        switch (type) {
            case 'character':
            case 'dialogue':
            case 'parenthetical':
                return 'flex justify-center'
            default:
                return ''
        }
    }

    // Get placeholder text
    const getPlaceholder = (type: ElementType): string => {
        switch (type) {
            case 'scene_heading':
                return 'INT. LOCATION - DAY'
            case 'action':
                return 'Action description...'
            case 'character':
                return 'CHARACTER NAME'
            case 'dialogue':
                return 'Dialogue...'
            case 'parenthetical':
                return '(parenthetical)'
            case 'transition':
                return 'CUT TO:'
            default:
                return 'Type here...'
        }
    }

    // Handle preview checkpoint - shows on main page
    const handlePreviewCheckpoint = (content: ScriptElement[], checkpointId: string) => {
        setPreviewSnapshot(content)
        setSelectedCheckpoint(checkpointId)
    }

    // Apply preview - restore to checkpoint
    const applyPreview = () => {
        if (previewSnapshot) {
            setElements([...previewSnapshot])
            setHasChanges(true)
            pushToHistory([...previewSnapshot])
            toast({ title: 'Revision restored', description: 'Your document has been restored to this version.' })
        }
        exitPreview()
    }

    // Exit preview mode
    const exitPreview = () => {
        setPreviewSnapshot(null)
        setSelectedCheckpoint(null)
    }

    // Toggle formatting
    const toggleFormatting = (format: 'bold' | 'italic' | 'underline' | 'strikethrough') => {
        if (!selectedElement) return

        const textarea = document.querySelector(`[data-element-id="${selectedElement}"]`) as HTMLTextAreaElement
        if (!textarea) return

        const start = textarea.selectionStart
        const end = textarea.selectionEnd
        const selectedText = textarea.value.substring(start, end)

        if (!selectedText) return

        const markers: Record<string, [string, string]> = {
            bold: ['**', '**'],
            italic: ['*', '*'],
            underline: ['_', '_'],
            strikethrough: ['~~', '~~'],
        }

        const [startMarker, endMarker] = markers[format]
        let newText: string
        let newStart: number, newEnd: number

        // Check if already formatted
        const beforeStart = textarea.value.substring(start - startMarker.length, start)
        const afterEnd = textarea.value.substring(end, end + endMarker.length)

        if (beforeStart === startMarker && afterEnd === endMarker) {
            // Remove formatting
            newText = textarea.value.substring(0, start - startMarker.length) +
                selectedText +
                textarea.value.substring(end + endMarker.length)
            newStart = start - startMarker.length
            newEnd = end - startMarker.length
        } else {
            // Add formatting
            newText = textarea.value.substring(0, start) +
                startMarker + selectedText + endMarker +
                textarea.value.substring(end)
            newStart = start + startMarker.length
            newEnd = end + startMarker.length
        }

        updateElement(selectedElement, newText)
        setTimeout(() => {
            textarea.focus()
            textarea.setSelectionRange(newStart, newEnd)
        }, 0)
    }

    // No loading state since we use localStorage
    // Data is available immediately

    // Format relative time - handles both Date and string
    const formatRelativeTime = (date: Date | string) => {
        const d = typeof date === 'string' ? new Date(date) : date
        const now = new Date()
        const diff = now.getTime() - d.getTime()
        if (diff < 60000) return 'Just now'
        if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`
        if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`
        return d.toLocaleDateString()
    }

    const displayElements = previewSnapshot || elements
    const pages = paginateElements()
    const currentElement = elements.find(el => el.id === selectedElement)
    const wordCount = displayElements.reduce((acc, el) => acc + el.content.split(/\s+/).filter(Boolean).length, 0)
    const currentPage = pages.findIndex(page => page.some(el => el.id === selectedElement)) + 1

    // Handle entering merge mode
    const handleMergeInitiate = async () => {
        if (!currentDraft?.parentDraftId) return

        // Fetch parent script content
        try {
            // Find parent draft's script
            const scripts = scriptsResponse?.data || []
            // We assume scripts are loaded. If the parent draft's script is in the same list?
            // Usually we fetch all scripts in project so yes.
            const parentScript = scripts.find(s => s.draftId === currentDraft.parentDraftId)

            if (parentScript) {
                // Get content (it might be JSON string or object depending on API)
                // Type is ScriptContent. 
                const content = parentScript.content.elements || [] // Accessing elements directly assuming it's parsed
                setParentScriptContent(content)
                setIsMergeView(true)
            } else {
                toast({ title: "Error", description: "Could not find parent script to merge with.", variant: "destructive" })
            }
        } catch (e) {
            console.error(e)
            toast({ title: "Error", description: "Failed to load parent script.", variant: "destructive" })
        }
    }

    const handleMergeConfirm = async () => {
        if (!currentDraft?.parentDraftId || !scriptId) return

        try {
            await api.post(`/projects/${projectId}/drafts/${draftId}/merge`, {
                targetDraftId: currentDraft.parentDraftId
            })
            toast({ title: "Merged Successfully", description: "Draft merged into parent." })
            // Redirect or refresh?
            // Maybe reload page or switch to parent draft?
            window.location.reload()
        } catch (e) {
            console.error(e)
            toast({ title: "Merge Failed", description: "Could not merge draft.", variant: "destructive" })
        }
    }

    return (
        <div className="h-full flex flex-row bg-background overflow-hidden">
            <div className={cn("flex-1 flex flex-col h-full overflow-hidden", focusMode && "focus-mode")}>
                {/* Title bar */}
                <div className={cn("flex items-center justify-between px-4 py-2 border-b bg-card/50", focusMode && "opacity-0 hover:opacity-100 transition-opacity")}>
                    <div className="flex items-center gap-3">
                        {isMergeView ? (
                            <div className="flex items-center gap-2">
                                <span className="text-sm font-medium text-indigo-400">Reviewing Merge</span>
                            </div>
                        ) : (
                            /* Save Status Indicator */
                            <div className="flex items-center gap-1.5 text-xs">
                                {saveStatus === 'saving' && (
                                    <>
                                        <Loader2 className="h-3 w-3 animate-spin text-muted-foreground" />
                                        <span className="text-muted-foreground">Saving...</span>
                                    </>
                                )}
                                {saveStatus === 'saved' && (
                                    <>
                                        <Check className="h-3 w-3 text-green-500" />
                                        <span className="text-green-500">Saved</span>
                                    </>
                                )}
                                {saveStatus === 'error' && (
                                    <>
                                        <CloudOff className="h-3 w-3 text-destructive" />
                                        <span className="text-destructive">Error</span>
                                    </>
                                )}
                                {saveStatus === 'idle' && hasChanges && (
                                    <>
                                        <Cloud className="h-3 w-3 text-muted-foreground" />
                                        <span className="text-muted-foreground animate-pulse">Editing...</span>
                                    </>
                                )}
                                {saveStatus === 'idle' && !hasChanges && lastSavedAt && (
                                    <span className="text-muted-foreground">Saved {formatRelativeTime(lastSavedAt)}</span>
                                )}
                            </div>
                        )}
                    </div>
                    <div className="flex items-center gap-2">
                        {isMergeView ? (
                            <>
                                <Button variant="ghost" onClick={() => setIsMergeView(false)}>Cancel</Button>
                                <Button onClick={handleMergeConfirm} className="bg-indigo-600 hover:bg-indigo-700 text-white">Merge into Main</Button>
                            </>
                        ) : (
                            <>
                                {isBranch && (
                                    <Button variant="outline" size="sm" className="h-8 mr-2" onClick={handleMergeInitiate}>
                                        Merge
                                    </Button>
                                )}
                                {selectedElements.size > 0 && (
                                    <span className="text-xs text-muted-foreground">{selectedElements.size} selected</span>
                                )}
                                <Button variant="ghost" size="sm" className="h-8" title="Export to Fountain" onClick={() => {
                                    const fountain = exportToFountain(elements, currentScript?.title, currentScript?.author || undefined)
                                    const blob = new Blob([fountain], { type: 'text/plain' })
                                    const url = URL.createObjectURL(blob)
                                    const a = document.createElement('a')
                                    a.href = url
                                    a.download = `${currentScript?.title || 'script'}.fountain`
                                    a.click()
                                    URL.revokeObjectURL(url)
                                    toast({ title: 'Exported', description: 'Script exported as Fountain file.' })
                                }}>
                                    <FileDown className="h-4 w-4" />
                                </Button>
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-8"
                                    onClick={() => setShowHistory(true)}
                                    title="Version History"
                                >
                                    <History className="h-4 w-4" />
                                </Button>
                            </>
                        )}
                    </div>
                </div>

                <div className="flex w-full h-full">
                    {/* Left Sidebar — hidden in focus mode */}
                    {!focusMode && showHistory && (
                        <div className="w-80 border-r overflow-y-auto bg-card">
                            {scriptId && (
                                <HistoryPanel
                                    scriptId={scriptId}
                                    selectedVersionId={selectedCheckpoint}
                                    onSelectVersion={(version: any) => {
                                        if (version) {
                                            let content: ScriptElement[] = [];
                                            try {
                                                content = typeof version.content === 'string'
                                                    ? JSON.parse(version.content)
                                                    : version.content;
                                            } catch (e) {
                                                console.error("Failed to parse version content", e);
                                                toast({ title: "Error", description: "Failed to load version content", variant: "destructive" });
                                                return;
                                            }
                                            handlePreviewCheckpoint(content, version.id);
                                        } else {
                                            exitPreview();
                                        }
                                    }}
                                />
                            )}
                        </div>
                    )}

                    {/* Main Editor or Diff View */}
                    <main className="flex-1 overflow-auto pb-20 relative" ref={editorContainerRef}>
                        {isMergeView && parentScriptContent ? (
                            <div className="h-full flex flex-col items-center bg-zinc-950 p-8">
                                <DiffViewer
                                    currentContent={elements}
                                    versionContent={parentScriptContent}
                                />
                            </div>
                        ) : previewSnapshot ? (
                            <DiffViewer
                                currentContent={elements}
                                versionContent={previewSnapshot}
                            />
                        ) : (
                            <div className="flex flex-col items-center py-6 gap-6">
                                {pages.map((pageElements, pageIndex) => (
                                    <div
                                        key={pageIndex}
                                        className="bg-zinc-900 border border-zinc-800 shadow-2xl relative flex-shrink-0 rounded"
                                        style={{
                                            width: `${PAGE_WIDTH_PX}px`,
                                            minHeight: `${PAGE_HEIGHT_PX}px`,
                                            fontFamily: "'Courier Prime', 'Courier New', Courier, monospace",
                                        }}
                                    >
                                        {/* Page number */}
                                        <div
                                            className="absolute text-xs text-zinc-600"
                                            style={{ top: `${PAGE_PADDING_TOP / 2}px`, right: `${PAGE_PADDING_RIGHT}px` }}
                                        >
                                            {pageIndex + 1}.
                                        </div>

                                        {/* Page content */}
                                        <div
                                            className="overflow-hidden"
                                            style={{
                                                padding: `${PAGE_PADDING_TOP}px ${PAGE_PADDING_RIGHT}px ${PAGE_PADDING_BOTTOM}px ${PAGE_PADDING_LEFT}px`,
                                                minHeight: `${PAGE_HEIGHT_PX}px`,
                                            }}
                                        >
                                            {pageElements.map((element) => {
                                                const globalIndex = elements.findIndex(el => el.id === element.id)
                                                const hasContd = shouldShowContd(elements, globalIndex)
                                                const sceneNum = sceneNumbers.get(element.id)
                                                return (
                                                    <div
                                                        key={element.id}
                                                        className={cn(
                                                            "relative group transition-all duration-75 cursor-text",
                                                            getElementContainerStyles(element.type),
                                                            selectedElement === element.id && "bg-zinc-800/60 -mx-2 px-2 rounded",
                                                            selectedElements.has(element.id) && "bg-primary/20 -mx-2 px-2 rounded ring-1 ring-primary/50",
                                                            focusMode && selectedElement !== element.id && "opacity-40 transition-opacity"
                                                        )}
                                                    >
                                                        {/* Scene number */}
                                                        {sceneNum !== undefined && (
                                                            <div className="absolute -left-16 top-0.5 text-[10px] text-zinc-500 font-mono select-none">
                                                                {sceneNum}
                                                            </div>
                                                        )}

                                                        {selectedElement === element.id && (
                                                            <div className="absolute -top-3 left-0 text-[9px] text-primary font-sans">
                                                                {ELEMENT_LABELS[element.type]}
                                                            </div>
                                                        )}

                                                        {/* Selection Handle (Gutter) */}
                                                        <div
                                                            className="absolute -left-12 top-0 bottom-0 w-10 cursor-pointer hover:bg-zinc-800/50 rounded"
                                                            onMouseDown={(e) => {
                                                                e.preventDefault()
                                                                handleDragStart(element.id)
                                                            }}
                                                            onMouseEnter={() => handleDragEnter(element.id)}
                                                        />

                                                        <textarea
                                                            data-element-id={element.id}
                                                            value={element.content}
                                                            onChange={(e) => updateElement(element.id, e.target.value)}
                                                            onFocus={() => {
                                                                setSelectedElement(element.id)
                                                                if (!selectedElements.has(element.id)) {
                                                                    setSelectedElements(new Set())
                                                                }
                                                            }}
                                                            onBlur={() => setAutocompleteVisible(false)}
                                                            onKeyDown={(e) => handleKeyDown(e, element.id, globalIndex)}
                                                            onInput={handleTextareaInput}
                                                            placeholder={getPlaceholder(element.type)}
                                                            rows={1}
                                                            className={cn(
                                                                "bg-transparent border-none outline-none resize-none text-[12pt] leading-tight py-0.5 text-zinc-100 placeholder:text-zinc-700",
                                                                getElementStyles(element.type)
                                                            )}
                                                            autoComplete="off"
                                                            spellCheck={true}
                                                            style={{ minHeight: '1.2em', overflow: 'hidden' }}
                                                        />

                                                        {/* CONT'D marker */}
                                                        {hasContd && (
                                                            <span className="absolute top-0.5 text-[12pt] text-zinc-400 font-mono pointer-events-none" style={{ right: '-80px' }}>
                                                                (CONT'D)
                                                            </span>
                                                        )}

                                                        {/* SmartType Autocomplete Dropdown */}
                                                        {autocompleteVisible && autocompleteTargetId === element.id && (
                                                            <div
                                                                ref={autocompleteRef}
                                                                className="absolute left-0 top-full z-50 mt-1 bg-zinc-800 border border-zinc-700 rounded-md shadow-xl overflow-hidden min-w-[200px]"
                                                            >
                                                                {autocompleteSuggestions.map((suggestion, idx) => (
                                                                    <button
                                                                        key={suggestion}
                                                                        className={cn(
                                                                            "w-full text-left px-3 py-1.5 text-sm font-mono text-zinc-200 hover:bg-zinc-700 transition-colors",
                                                                            idx === autocompleteIndex && "bg-primary/30 text-white"
                                                                        )}
                                                                        onMouseDown={(e) => {
                                                                            e.preventDefault()
                                                                            updateElement(element.id, suggestion)
                                                                            setAutocompleteVisible(false)
                                                                        }}
                                                                    >
                                                                        {suggestion}
                                                                    </button>
                                                                ))}
                                                                <div className="px-3 py-1 text-[10px] text-zinc-500 border-t border-zinc-700">
                                                                    Tab to accept &bull; Esc to dismiss
                                                                </div>
                                                            </div>
                                                        )}
                                                    </div>
                                                )
                                            })}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </main>
                </div>

                {/* Bottom Toolbar */}
                <footer className={cn("fixed bottom-4 left-1/2 -translate-x-1/2 z-50", focusMode && "opacity-0 hover:opacity-100 transition-opacity")}>
                    <div className="flex items-center gap-1 bg-zinc-900 border border-zinc-700 rounded-lg shadow-2xl px-3 py-2">
                        <Button
                            variant="ghost"
                            size="icon"
                            className={cn("h-8 w-8", historyIndex > 0 ? "text-zinc-400 hover:text-white" : "text-zinc-600")}
                            title="Undo (⌘Z)"
                            onClick={handleUndo}
                            disabled={historyIndex <= 0}
                        >
                            <Undo className="h-4 w-4" />
                        </Button>
                        <Button
                            variant="ghost"
                            size="icon"
                            className={cn("h-8 w-8", historyIndex < history.length - 1 ? "text-zinc-400 hover:text-white" : "text-zinc-600")}
                            title="Redo (⌘⇧Z)"
                            onClick={handleRedo}
                            disabled={historyIndex >= history.length - 1}
                        >
                            <Redo className="h-4 w-4" />
                        </Button>

                        <div className="w-px h-5 bg-zinc-700 mx-1" />

                        {(['scene_heading', 'action', 'character', 'dialogue', 'parenthetical', 'transition'] as ElementType[]).map((type) => (
                            <Button
                                key={type}
                                variant="ghost"
                                size="sm"
                                className={cn(
                                    "h-7 px-2 text-xs",
                                    currentElement?.type === type
                                        ? "bg-zinc-700 text-white"
                                        : "text-zinc-400 hover:text-white"
                                )}
                                onClick={() => {
                                    if (selectedElement) {
                                        changeElementType(selectedElement, type)
                                    }
                                }}
                            >
                                {ELEMENT_LABELS[type]}
                            </Button>
                        ))}

                        <div className="w-px h-5 bg-zinc-700 mx-1" />

                        <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-zinc-400 hover:text-white"
                            title="Bold (⌘B)"
                            onClick={() => toggleFormatting('bold')}
                        >
                            <Bold className="h-4 w-4" />
                        </Button>
                        <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-zinc-400 hover:text-white"
                            title="Italic (⌘I)"
                            onClick={() => toggleFormatting('italic')}
                        >
                            <Italic className="h-4 w-4" />
                        </Button>
                        <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-zinc-400 hover:text-white"
                            title="Underline (⌘U)"
                            onClick={() => toggleFormatting('underline')}
                        >
                            <Underline className="h-4 w-4" />
                        </Button>
                        <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-zinc-400 hover:text-white"
                            title="Strikethrough"
                            onClick={() => toggleFormatting('strikethrough')}
                        >
                            <Strikethrough className="h-4 w-4" />
                        </Button>

                        <div className="w-px h-5 bg-zinc-700 mx-1" />

                        <div className="text-xs text-zinc-500 px-2">
                            {wordCount}w &bull; Page {currentPage}/{pages.length}
                        </div>

                        <div className="w-px h-5 bg-zinc-700 mx-1" />

                        {/* Focus Mode Toggle */}
                        <Button
                            variant="ghost"
                            size="icon"
                            className={cn("h-8 w-8", focusMode ? "text-primary" : "text-zinc-400 hover:text-white")}
                            title={focusMode ? "Exit Focus Mode (⌘D)" : "Focus Mode (⌘D)"}
                            onClick={() => setFocusMode(!focusMode)}
                        >
                            {focusMode ? <Minimize className="h-4 w-4" /> : <Maximize className="h-4 w-4" />}
                        </Button>

                        <Button variant="ghost" size="icon" className="h-8 w-8 text-zinc-400 hover:text-white" title="Comments">
                            <MessageSquare className="h-4 w-4" />
                        </Button>
                    </div>
                </footer>

                {/* Preview Mode Floating Bar */}
                {previewSnapshot && (
                    <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-50">
                        <div className="flex items-center gap-3 bg-card border border-primary/50 rounded-lg shadow-2xl px-4 py-3">
                            <div className="flex items-center gap-2">
                                <History className="h-4 w-4 text-primary" />
                                <span className="text-sm font-medium">Previewing checkpoint</span>
                                <span className="text-xs text-muted-foreground">
                                    ({previewSnapshot.length} elements)
                                </span>
                            </div>
                            <div className="w-px h-5 bg-border" />
                            <Button variant="outline" size="sm" onClick={exitPreview}>
                                Cancel
                            </Button>
                            <Button size="sm" onClick={applyPreview}>
                                <Check className="h-3 w-3 mr-1" />
                                Apply this version
                            </Button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    )
}
